"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PackageDataAuditResponseSchema = exports.PackageDataAuditUnresolvableResponseSchema = exports.PackageDataAuditHintSchema = exports.PackageDataAuditAnsweredResponseSchema = exports.PackageDataAuditItemResponseSchema = exports.PackageDataAuditOpinionItemSchema = exports.PackageDataAuditAdjudicatedItemSchema = exports.PackageDataAuditMetadataSchema = exports.ProposedValueAssessmentSchema = exports.PackageDataAuditRequestSchema = exports.PackageDataAuditRequestShape = exports.PackageDataAuditItemShape = exports.ProposedValueVerdictSchema = exports.ProposedValueVerdict = exports.PackageDataAuditItemStatusSchema = exports.PackageDataAuditItemStatus = exports.PackageDataAuditStatusSchema = exports.PackageDataAuditStatus = exports.PackageDataAuditQuestionSchema = exports.PackageDataAuditQuestion = exports.PACKAGE_DATA_AUDIT_MAX_ITEMS = void 0;
exports.refinePackageDataAuditRequest = refinePackageDataAuditRequest;
exports.toPackageDataAuditItemResponse = toPackageDataAuditItemResponse;
exports.toPackageDataAuditResponse = toPackageDataAuditResponse;
const z = require("zod");
const consensus_types_1 = require("../Consensus/consensus.types");
const property_key_1 = require("../Consensus/property-key");
const slice_key_1 = require("../Consensus/slice-key");
const adjudication_request_types_1 = require("./adjudication-request.types");
exports.PACKAGE_DATA_AUDIT_MAX_ITEMS = 50;
var PackageDataAuditQuestion;
(function (PackageDataAuditQuestion) {
    PackageDataAuditQuestion["Value"] = "value";
    PackageDataAuditQuestion["Existence"] = "existence";
})(PackageDataAuditQuestion || (exports.PackageDataAuditQuestion = PackageDataAuditQuestion = {}));
exports.PackageDataAuditQuestionSchema = z.nativeEnum(PackageDataAuditQuestion);
var PackageDataAuditStatus;
(function (PackageDataAuditStatus) {
    PackageDataAuditStatus["Answered"] = "answered";
    PackageDataAuditStatus["RequestUnresolvable"] = "request_unresolvable";
})(PackageDataAuditStatus || (exports.PackageDataAuditStatus = PackageDataAuditStatus = {}));
exports.PackageDataAuditStatusSchema = z.nativeEnum(PackageDataAuditStatus);
var PackageDataAuditItemStatus;
(function (PackageDataAuditItemStatus) {
    PackageDataAuditItemStatus["Adjudicated"] = "adjudicated";
    PackageDataAuditItemStatus["AdjudicationPending"] = "adjudication_pending";
    PackageDataAuditItemStatus["NotAdjudicated"] = "not_adjudicated";
})(PackageDataAuditItemStatus || (exports.PackageDataAuditItemStatus = PackageDataAuditItemStatus = {}));
exports.PackageDataAuditItemStatusSchema = z.nativeEnum(PackageDataAuditItemStatus);
var ProposedValueVerdict;
(function (ProposedValueVerdict) {
    ProposedValueVerdict["Incorrect"] = "incorrect";
})(ProposedValueVerdict || (exports.ProposedValueVerdict = ProposedValueVerdict = {}));
exports.ProposedValueVerdictSchema = z.nativeEnum(ProposedValueVerdict);
exports.PackageDataAuditItemShape = z.object({
    row: z
        .record(z.string(), z.string())
        .describe('Facts identifying the row, keyed by column name, such as { "signalName": "NRST" } or { "pinId": "A3" }. ' +
        "The property's identity columns locate the row when given. Without them, the value columns you " +
        'give locate it instead and must match exactly one row. Other keys are ignored.'),
    question: exports.PackageDataAuditQuestionSchema.describe('"value" asks whether one cell is right; "existence" asks whether the row should be in the table at all.'),
    column: z
        .string()
        .min(1)
        .optional()
        .describe('The column in doubt. Required for a value question; omit for an existence question.'),
    observedValue: z
        .string()
        .optional()
        .describe('The value you see at that cell. Send it whenever you have it: a mismatch means the row resolved ' +
        'wrongly or your read is stale.'),
    proposedValue: z
        .string()
        .optional()
        .describe('What you think the cell should hold, only when you have a specific alternative in mind. ' +
        'For an existence question, "absent" or "present".'),
    concern: z
        .string()
        .trim()
        .min(1)
        .max(adjudication_request_types_1.ADJUDICATION_CONCERN_MAX_LENGTH)
        .optional()
        .describe('Why you believe the value is wrong. The expert who reviews the cell reads this when your call raises ' +
        `the review. At most ${adjudication_request_types_1.ADJUDICATION_CONCERN_MAX_LENGTH} characters.`),
});
exports.PackageDataAuditRequestShape = z.object({
    packageId: z.string().min(1).describe('The package whose data is in doubt.'),
    propertyKey: property_key_1.PropertyKeySchema.describe('The package-data property whose table the items belong to.'),
    items: z
        .array(exports.PackageDataAuditItemShape)
        .min(1)
        .max(exports.PACKAGE_DATA_AUDIT_MAX_ITEMS)
        .describe('Every cell or row of this table you doubt, one item each. All items are placed together or the ' +
        `request is refused as a whole; at most ${exports.PACKAGE_DATA_AUDIT_MAX_ITEMS} per call.`),
});
function refinePackageDataAuditRequest(request, ctx) {
    request.items.forEach((item, index) => {
        if (item.question === PackageDataAuditQuestion.Value && item.column === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Item ${index}: a value question names the column in doubt.`,
                path: ['items', index, 'column'],
            });
        }
        if (item.question === PackageDataAuditQuestion.Existence && item.column !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Item ${index}: an existence question is about the whole row and takes no column.`,
                path: ['items', index, 'column'],
            });
        }
    });
}
exports.PackageDataAuditRequestSchema = exports.PackageDataAuditRequestShape.superRefine(refinePackageDataAuditRequest);
exports.ProposedValueAssessmentSchema = z.object({
    verdict: exports.ProposedValueVerdictSchema,
    reason: z.string(),
});
exports.PackageDataAuditMetadataSchema = z.object({
    sliceKey: slice_key_1.SliceKeySchema,
    sliceVersion: z.number().int(),
    anchor: consensus_types_1.AnchorSchema,
    requestId: z.string().optional(),
    planId: z.string().optional(),
    citedClauseIds: z.string().array(),
});
exports.PackageDataAuditAdjudicatedItemSchema = z.object({
    status: z.literal(PackageDataAuditItemStatus.Adjudicated),
    subject: z.string(),
    value: z.string(),
    explanation: z.string(),
    proposedValueAssessment: exports.ProposedValueAssessmentSchema.optional(),
});
exports.PackageDataAuditOpinionItemSchema = z.object({
    status: z.union([
        z.literal(PackageDataAuditItemStatus.AdjudicationPending),
        z.literal(PackageDataAuditItemStatus.NotAdjudicated),
    ]),
    subject: z.string(),
    value: z.string().optional(),
    explanation: z.string().optional(),
});
exports.PackageDataAuditItemResponseSchema = z.union([
    exports.PackageDataAuditAdjudicatedItemSchema,
    exports.PackageDataAuditOpinionItemSchema,
]);
exports.PackageDataAuditAnsweredResponseSchema = z.object({
    status: z.literal(PackageDataAuditStatus.Answered),
    items: exports.PackageDataAuditItemResponseSchema.array(),
});
exports.PackageDataAuditHintSchema = z.object({
    index: z.number().int().nonnegative().optional(),
    hint: z.string(),
});
exports.PackageDataAuditUnresolvableResponseSchema = z.object({
    status: z.literal(PackageDataAuditStatus.RequestUnresolvable),
    hints: exports.PackageDataAuditHintSchema.array().min(1),
});
exports.PackageDataAuditResponseSchema = z.union([
    exports.PackageDataAuditAnsweredResponseSchema,
    exports.PackageDataAuditUnresolvableResponseSchema,
]);
function toPackageDataAuditItemResponse(item) {
    switch (item.status) {
        case PackageDataAuditItemStatus.Adjudicated:
            return {
                status: item.status,
                subject: item.subject,
                value: item.value,
                explanation: item.explanation,
                ...(item.proposedValueAssessment === undefined ?
                    {}
                    : { proposedValueAssessment: item.proposedValueAssessment }),
            };
        case PackageDataAuditItemStatus.AdjudicationPending:
        case PackageDataAuditItemStatus.NotAdjudicated:
            return {
                status: item.status,
                subject: item.subject,
                ...(item.value === undefined ? {} : { value: item.value }),
                ...(item.explanation === undefined ? {} : { explanation: item.explanation }),
            };
    }
}
function toPackageDataAuditResponse(result) {
    if (result.status === PackageDataAuditStatus.RequestUnresolvable) {
        return {
            status: result.status,
            hints: result.hints.map((entry) => entry.index === undefined ? { hint: entry.hint } : { index: entry.index, hint: entry.hint }),
        };
    }
    return { status: result.status, items: result.items.map(toPackageDataAuditItemResponse) };
}
//# sourceMappingURL=package-data-audit.types.js.map